import {get} from "lodash"
import React, {FC, useState} from "react"
import styled from "styled-components"

import Button, {IconTypes} from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import Icon from "@irati/ui/src/Icon"
import Card from "@irati/ui/src/Layout/Card"
import {success} from "@irati/utils/notify"
import Loading, {ErrorProps} from "@irati/ui/src/Loading"
import Loader from "@irati/ui/src/Loading/Loader"
import LoadingError from "@irati/ui/src/Loading/Error"
import {Body, H5} from "@irati/ui/src/Typography"
import id from "@irati/utils/id"
import {FormattedMessage} from "@irati/utils/lang"
import {InjectIntlProps, injectIntl} from "@irati/utils/lang/injectIntl"

/** This keeps the loader centered and proportioned in a stretched div */
const LoaderWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const StyledFlex = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: ${({theme}) => theme.spacing.oneAndHalf};

  & > *:last-child {
    flex: 1;
  }
`

interface ErrorCompProps {
  errorProps: ErrorProps
  reset: () => void
  retry: () => void
  idNamespace: any
}

const ErrorComp: FC<ErrorCompProps> = ({errorProps, reset, retry, idNamespace}) => {
  // HACK: raised https://huddlebuy.atlassian.net/browse/MRW-352 to expose this error code from BE
  const hasNoReport = get(errorProps, "error.networkError.errorCode") === "PB_REPORTING_NotFound"
  return (
    <Flex direction="column" justifyContent="space-between" alignItems="flex-start" gap={1.5} height="100%">
      {hasNoReport ? (
        <Body>
          <FormattedMessage id={"platform.wallet.reward.reports.cardNoReport"} />
        </Body>
      ) : (
        <LoadingError {...errorProps} />
      )}
      <Flex gap={4}>
        <Button id={idNamespace} tertiary onClick={retry}>
          <FormattedMessage id={"platform.wallet.reward.reports.cardTryAgain"} />
        </Button>
        <Button id={idNamespace} tertiary onClick={reset}>
          <FormattedMessage id={"platform.wallet.reward.reports.cardCancel"} />
        </Button>
      </Flex>
    </Flex>
  )
}

interface Props extends InjectIntlProps {
  reportId: string
  title: string
  body: string
  onClick: () => Promise<void>
}

const ReportingCard: FC<Props> = ({reportId, title, body, onClick, formatMessage}) => {
  const [loadingState, setLoading] = useState(Promise.resolve())
  const idNamespace = id("reward-wallet-reports", reportId)

  const handleOnClick = () => {
    setLoading(
      onClick().then(() => {
        success({message: formatMessage({id: "platform.wallet.reward.reports.cardSuccess"})})
      })
    )
  }

  return (
    <Card>
      <StyledFlex>
        <H5 marginBottom>
          <FormattedMessage id={title} />
        </H5>
        <Loading
          load={loadingState}
          Loader={(props: any) => (
            <LoaderWrapper>
              <Loader {...props} />
            </LoaderWrapper>
          )}
          Error={(errorProps: any) => (
            <ErrorComp
              errorProps={errorProps}
              idNamespace={idNamespace}
              reset={() => setLoading(Promise.resolve())}
              retry={handleOnClick}
            />
          )}>
          <Flex direction="column" justifyContent="space-between" alignItems="flex-start" gap={1.5} height="100%">
            <Body>
              <FormattedMessage id={body} />
            </Body>
            <Button id={idNamespace} tertiary onClick={handleOnClick}>
              <FormattedMessage id="platform.wallet.tenant.downloadReport.button" />
              <Icon icon={IconTypes.downloadReport} marginLeft />
            </Button>
          </Flex>
        </Loading>
      </StyledFlex>
    </Card>
  )
}

export default injectIntl(ReportingCard)
